import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Form,
  Input,
  Button,
  Toast,
  Popup,
  SearchBar,
  List,
  DotLoading,
} from 'antd-mobile';
import { authApi } from '../api';
import api from '../api';

const EN_TO_RU = {
  q: 'й', w: 'ц', e: 'у', r: 'к', t: 'е', y: 'н', u: 'г', i: 'ш', o: 'щ', p: 'з',
  '[': 'х', ']': 'ъ', a: 'ф', s: 'ы', d: 'в', f: 'а', g: 'п', h: 'р', j: 'о',
  k: 'л', l: 'д', ';': 'ж', "'": 'э', '\\': '\\', z: 'я', x: 'ч', c: 'с', v: 'м',
  b: 'и', n: 'т', m: 'ь', ',': 'б', '.': 'ю', '/': '.',
  Q: 'Й', W: 'Ц', E: 'У', R: 'К', T: 'Е', Y: 'Н', U: 'Г', I: 'Ш', O: 'Щ', P: 'З',
  '{': 'Х', '}': 'Ъ', A: 'Ф', S: 'Ы', D: 'В', F: 'А', G: 'П', H: 'Р', J: 'О',
  K: 'Л', L: 'Д', ':': 'Ж', '"': 'Э', '|': '/', Z: 'Я', X: 'Ч', C: 'С', V: 'М',
  B: 'И', N: 'Т', M: 'Ь', '<': 'Б', '>': 'Ю', '?': ',',
};

function toRussianLayout(text) {
  return text
    .split('')
    .map((c) => EN_TO_RU[c] || c)
    .join('');
}

function matchesQuery(employee, query) {
  if (!query) return true;
  const q = query.toLowerCase().trim();
  if (!q) return true;
  const ruQ = toRussianLayout(q);
  const haystack = (employee.full_name || '').toLowerCase();
  return haystack.includes(q) || haystack.includes(ruQ);
}

function LoginPage() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [mode, setMode] = useState('email');
  const [employees, setEmployees] = useState([]);
  const [loadingUsers, setLoadingUsers] = useState(true);
  const [pickerVisible, setPickerVisible] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [form] = Form.useForm();

  useEffect(() => {
    api
      .get('/hr/employees?include_demo=true')
      .then((res) => {
        const list = (res.data.employees || []).slice();
        list.sort((a, b) =>
          (a.full_name || '').localeCompare(b.full_name || '', 'ru')
        );
        setEmployees(list);
      })
      .catch(() => {
        Toast.show({ icon: 'fail', content: 'Не удалось загрузить пользователей' });
        setEmployees([]);
      })
      .finally(() => setLoadingUsers(false));
  }, []);

  const filteredEmployees = useMemo(
    () => employees.filter((emp) => matchesQuery(emp, searchQuery)),
    [employees, searchQuery]
  );

  const selectedName = Form.useWatch('username', form);
  const selectedEmployee = useMemo(
    () => employees.find((emp) => emp.full_name === selectedName),
    [employees, selectedName]
  );

  const handleSelectEmployee = (emp) => {
    if (emp.email) {
      setMode('email');
      form.setFieldsValue({ username: emp.email });
    } else {
      setMode('name');
      form.setFieldsValue({ username: emp.full_name });
    }
    setSearchQuery('');
    setPickerVisible(false);
  };

  const onFinish = async (values) => {
    setLoading(true);
    try {
      const res = await authApi.login(values.username, values.password);
      localStorage.setItem('token', res.data.access_token);
      localStorage.setItem('user', JSON.stringify(res.data.user));
      if (res.data.user.must_change_password) {
        navigate('/change-password');
      } else {
        Toast.show({ icon: 'success', content: 'Добро пожаловать!' });
        navigate('/');
      }
    } catch (err) {
      Toast.show({
        icon: 'fail',
        content: err?.response?.data?.detail || 'Ошибка авторизации',
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div
      style={{
        minHeight: '100vh',
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'space-evenly',
        padding: 24,
        boxSizing: 'border-box',
      }}
    >
      <img
        src="/logo.jpg"
        alt="Ботаник Go"
        style={{
          display: 'block',
          width: '75%',
          height: 'auto',
          margin: '0 auto',
        }}
      />

      <div
        style={{
          background: '#e6f4ff',
          border: '1px solid #91caff',
          borderRadius: 8,
          padding: 12,
          margin: '0 0 16px 0',
          color: '#0958d9',
          fontSize: 13,
          lineHeight: 1.5,
        }}
      >
        <strong style={{ display: 'block', marginBottom: 6 }}>Внимание! Изменился способ входа.</strong>
        <ul style={{ margin: 0, paddingLeft: 16 }}>
          <li>Если у вас указан корпоративный email — введите его и пароль.</li>
          <li>Если email ещё не заполнен — нажмите «Найти себя по ФИО», выберите себя в списке и укажите email при первом входе.</li>
          <li>Ваш текущий пароль остаётся действительным.</li>
        </ul>
      </div>

      <Form
        form={form}
        layout="vertical"
        onFinish={onFinish}
        footer={
          <Button block type="button" color="primary" loading={loading} size="large" onClick={() => form.submit()}>
            Войти
          </Button>
        }
      >
        <Form.Item
          name="username"
          label={mode === 'email' ? 'Email' : 'Сотрудник'}
          rules={[
            {
              required: true,
              message: mode === 'email' ? 'Введите email' : 'Выберите сотрудника',
            },
          ]}
        >
          {mode === 'email' ? (
            <Input type="email" placeholder="ivanov.ii@str.mos.ru" />
          ) : (
            <div
              onClick={() => setPickerVisible(true)}
              style={{
                width: '100%',
                padding: '12px 14px',
                background: '#f5f5f5',
                border: '1px solid #e0e0e0',
                borderRadius: 8,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                fontSize: 15,
                color: selectedEmployee ? '#111' : '#999',
                boxSizing: 'border-box',
              }}
            >
              <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                {loadingUsers
                  ? 'Загрузка...'
                  : selectedEmployee
                  ? selectedEmployee.full_name
                  : 'Выберите пользователя'}
              </span>
              <span style={{ color: '#999', marginLeft: 8 }}>▾</span>
            </div>
          )}
        </Form.Item>

        <Form.Item
          name="password"
          label="Пароль"
          rules={[{ required: true, message: 'Введите пароль' }]}
        >
          <Input type="password" />
        </Form.Item>
      </Form>

      <div style={{ textAlign: 'center', marginBottom: 16 }}>
        <Button
          fill="none"
          size="small"
          onClick={() => {
            form.setFieldsValue({ username: '' });
            setSearchQuery('');
            setMode(mode === 'email' ? 'name' : 'email');
          }}
        >
          {mode === 'email' ? 'Нет email? Найти себя по ФИО' : 'Войти по email'}
        </Button>
      </div>

      <Popup
        visible={pickerVisible}
        onMaskClick={() => setPickerVisible(false)}
        onClose={() => setPickerVisible(false)}
        position="bottom"
        bodyStyle={{ height: '70vh' }}
      >
        <div
          style={{
            padding: '12px 16px',
            borderBottom: '1px solid #eee',
          }}
        >
          <SearchBar
            placeholder="Поиск по ФИО"
            value={searchQuery}
            onChange={(v) => setSearchQuery(v)}
          />
        </div>
        <div style={{ overflowY: 'auto', height: 'calc(70vh - 110px)' }}>
          {loadingUsers ? (
            <div style={{ textAlign: 'center', padding: 40, color: '#999' }}>
              <DotLoading />
              <div style={{ marginTop: 8 }}>Загрузка пользователей...</div>
            </div>
          ) : filteredEmployees.length === 0 ? (
            <div style={{ textAlign: 'center', padding: 40, color: '#999' }}>
              Ничего не найдено
            </div>
          ) : (
            <List>
              {filteredEmployees.map((emp) => (
                <List.Item
                  key={emp.id}
                  onClick={() => handleSelectEmployee(emp)}
                  arrow={selectedEmployee?.id === emp.id}
                >
                  <span
                    style={{
                      color: selectedEmployee?.id === emp.id ? '#1677ff' : '#111',
                      fontWeight: selectedEmployee?.id === emp.id ? 600 : 400,
                    }}
                  >
                    {emp.full_name}
                  </span>
                </List.Item>
              ))}
            </List>
          )}
        </div>
        <div
          style={{
            padding: '10px 16px',
            textAlign: 'right',
            color: '#999',
            fontSize: 12,
            borderTop: '1px solid #eee',
          }}
        >
          {filteredEmployees.length} из {employees.length}
        </div>
      </Popup>
    </div>
  );
}

export default LoginPage;
